import * as yup from 'yup';

// Base schema for shared fields.
export const baseSchema = yup.object({
  firstName: yup.string()
    .trim()
    .matches(/^[a-zA-Z]+$/, 'First name must contain only letters')
    .required('First name is required'),
  lastName: yup.string()
    .trim()
    .matches(/^[a-zA-Z]+$/, 'Last name must contain only letters')
    .required('Last name is required'),
  email: yup.string()
    .trim()
    .email('Invalid email address')
    .required('Email is required'),
  phone: yup.string()
    .trim()
    .matches(/^\+?[0-9\s\-]{7,}$/, 'Invalid phone number')
    .required('Phone number is required')
});

// Define common field fragments.
const submissionTypeField = yup.string().required('Submission type is required');
const venueField = yup.string().required('Please select a venue');
const showNameFieldOptional = yup.string().trim();
const showNameFieldRequired = yup.string().required('Show name is required');
const enquiryField = yup.string().trim().required('Enquiry message is required');

const buildSchema = (fields) => 
  baseSchema.concat(yup.object(fields)).noUnknown(true, 'Invalid extra field provided');

// General Enquiry Form Schema
export const generalEnquirySchema = buildSchema({
  submissionType: submissionTypeField,
  venue: venueField,
  showName: showNameFieldOptional,
  enquiry: enquiryField
});

// Accessibility / Companion Card Booking Schema
export const accessibilitySchema = buildSchema({
  submissionType: submissionTypeField,
  venue: venueField,
  showName: showNameFieldRequired,
  showDate: yup.date().required('Preferred show date is required'),
  companion: yup.string().trim(), 
  tickets: yup.number()
    .required('Number of tickets is required')
    .min(1, 'Number of tickets must be at least 1')
    .max(20, 'Number of tickets must be 20 or fewer'),
  enquiry: enquiryField
});

// Events, Functions or Hospitality Enquiry Schema
export const eventsSchema = buildSchema({
  submissionType: submissionTypeField,
  company: yup.string().trim().required('Company name is required'),
  venue: venueField,
  showName: showNameFieldRequired,
  showDate: yup.date().required('Preferred show date is required'),
  showTime: yup.string().required('Preferred show time is required'),
  guests: yup.number().required('Number of guests is required'),
  alreadyPurchased: yup.string().required('Ticket purchase confirmation is required'),
  foodBeverage: yup.string().trim(),
  additionalInfo: yup.string().trim()
});

// Lost Property Enquiry Schema
export const lostPropertySchema = buildSchema({
  submissionType: submissionTypeField,
  venue: venueField,
  showName: showNameFieldRequired,
  showDate: yup.date().required('Visited show date is required'),
  showTime: yup.string().required('Visited show time is required'),
  seatingAllocation: yup.string().trim().required('Seating allocation is required'),
  itemDescription: yup.string().trim().required('Item description is required')
});

// Recent Visit Feedback Schema
export const recentVisitSchema = buildSchema({
  submissionType: submissionTypeField,
  venue: venueField,
  showDate: yup.date().required('Date visited is required'),
  showTime: yup.string().required('Visited show time is required'),
  seatingAllocation: yup.string().trim().required('Seating allocation is required'),
  note: yup.string().trim().required('Note is required')
});

// Ticket Purchase Assistance Schema
export const ticketAssistanceSchema = buildSchema({
  submissionType: submissionTypeField,
  venue: venueField,
  showName: showNameFieldRequired,
  showDate: yup.date().required('Preferred show date is required'),
  showTime: yup.string().required('Preferred show time is required'),
  tickets: yup.number()
    .required('Number of tickets is required')
    .min(1, 'Number of tickets must be at least 1')
    .max(20, 'Number of tickets must be 20 or fewer'),
  seating: yup.string().required('Desired seating location is required')
});

// Venue Hire and Theatre Programming Enquiry Schema
export const venueHireSchema = buildSchema({
  submissionType: submissionTypeField,
  company: yup.string().trim().required('Company name is required'),
  venue: venueField,
  showDate: yup.date().required('Preferred show date is required'),
  showTime: yup.string().required('Preferred show time is required'),
  additionalInfo: yup.string().trim()
});

// Employment form schema
export const employmentSchema = buildSchema({
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
  email: yup.string().required('Email is required'),
  phone: yup.string().required('Phone number is required'),
  submissionType: yup.string().required(),
  streetAddress: yup.string().required('Street address is required'),
  suburb: yup.string().required('Suburb is required'),
  postcode: yup.string()
    .matches(/^\d{4}$/, 'Postcode must be 4 digits')
    .required('Postcode is required'),
  over18: yup.string()
    .oneOf(['Yes', 'No'], 'Age verification is required')
    .required('Age verification is required'),
  rsa: yup.string()
    .oneOf(['Yes', 'No'], 'RSA status is required')
    .required('RSA status is required'),
  resident: yup.string()
    .oneOf(['Yes', 'No'], 'Residency status is required')
    .required('Residency status is required'),
  visaType: yup.string().when('resident', (resident, schema) => {
    return resident === 'No'
      ? schema.required('Visa type is required for non-residents')
      : schema;
  }),
  workedBefore: yup.string()
    .oneOf(['Yes', 'No'], 'Previous employment status is required')
    .required('Previous employment status is required'),
  appliedBefore: yup.string()
    .oneOf(['Yes', 'No'], 'Previous application status is required')
    .required('Previous application status is required'),
  department: yup.string()
    .oneOf(['Food & Beverage', 'Patron Services'], 'Department selection is required')
    .required('Department selection is required'),
  employmentType: yup.string()
    .oneOf(['Casual', 'Permanent', 'Either'], 'Employment type is required')
    .required('Employment type is required'),
  jobHeard: yup.string().required('Job source information is required'),
  whyWork: yup.string().required('Motivation for applying is required'),
  hobbies: yup.string().required('Hobbies information is required'),
  terms: yup.boolean().oneOf([true], 'You must agree to the terms and conditions')
});
